import React from "react";
import { Card, CardBody, CardHeader } from "reactstrap";

const OccupancyChart = ({ data }) => {
    const { totalSpots = 0, occupiedSpots = 0, history = [] } = data || {};
    
    // Calcula o teto (maior capacidade já registrada nos 12 meses) para escalonar o eixo Y corretamente
    // Garante no mínimo 10 para evitar divisões por zero ou gráficos quebrados se base zerada
    const maxCapacity = Math.max(...history.map(d => d.capacity || 0), totalSpots, 10);
    
    return (
        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "4px" }}>
            <CardHeader className="bg-white border-0 pt-4 px-4 pb-0">
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                    <h5 className="fw-bold m-0 text-primary" style={{ fontSize: "18px" }}>
                        Histórico de Ocupação Geral
                    </h5>
                    <div className="d-flex align-items-center gap-4">
                        <div className="text-end">
                            <small className="text-muted d-block fw-bold font-size-11 text-uppercase">Mês Atual</small>
                            <span className="fw-bold font-size-18" style={{ color: "#3b82f6" }}>
                                {occupiedSpots} <span className="text-muted font-size-14">/ {totalSpots} vagas</span>
                            </span>
                        </div>
                    </div>
                </div>
            </CardHeader>

            <CardBody className="p-4 pt-2">
                <div style={{ height: "320px", position: "relative", paddingLeft: "45px", paddingRight: "45px", marginTop: "10px" }}>
                    {/* Eixo Y (Capacidade Máxima) */}
                    <div style={{ position: "absolute", left: -10, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", fontSize: "11px", color: "#94a3b8", fontWeight: "600", paddingBottom: "35px" }}>
                        <span>{Math.round(maxCapacity)}</span>
                        <span>{Math.round(maxCapacity * 0.75)}</span>
                        <span>{Math.round(maxCapacity * 0.50)}</span>
                        <span>{Math.round(maxCapacity * 0.25)}</span>
                        <span>0</span>
                    </div>

                    {/* Eixo Y (% Percentual a direita para referencia) */}
                    <div style={{ position: "absolute", right: -5, height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between", fontSize: "11px", color: "#94a3b8", fontWeight: "600", paddingBottom: "35px" }}>
                        <span>100%</span>
                        <span>75%</span>
                        <span>50%</span>
                        <span>25%</span>
                        <span>0%</span>
                    </div>

                    {/* Linhas de Grade */}
                    <div style={{ position: "absolute", top: 0, left: "45px", right: "45px", bottom: "35px", display: "flex", flexDirection: "column", justifyContent: "space-between", zIndex: 0 }}>
                        {[0, 1, 2, 3, 4].map(i => <div key={i} style={{ borderBottom: "1px dashed #e2e8f0", width: "100%", height: "1px" }} />)}
                    </div>

                    {/* Barras */}
                    <div className="h-100 w-100 d-flex position-relative align-items-end" style={{ zIndex: 1, paddingBottom: "35px" }}>
                        {history.map((item, idx) => {
                            const capHeight = (item.capacity / maxCapacity) * 100;
                            const enrolledHeight = (item.enrolled / maxCapacity) * 100;
                            const isCurrentMonth = idx === history.length - 1;

                            // Cores em tons pastéis com base na superlotação
                            // Verde pastel (Tranquilo), Laranja pastel (Atenção), Vermelho pastel (Lotado)
                            let barColor = "#6ee7b7"; 
                            if (item.pct >= 90) barColor = "#fca5a5"; 
                            else if (item.pct >= 75) barColor = "#fdba74"; 

                            return (
                                <div key={idx} className="flex-grow-1 h-100 d-flex flex-column align-items-center position-relative">
                                    <div className="d-flex flex-column justify-content-end h-100 w-100 align-items-center"
                                         title={`${item.month}: ${item.enrolled} matriculados de ${item.capacity} vagas (${item.pct}%)`}
                                         style={{ cursor: "pointer" }}>
                                        
                                        {/* Barra de Capacidade Total (Fundo Cinza Claro) */}
                                        <div
                                            style={{
                                                width: "40%",
                                                height: `${capHeight}%`,
                                                backgroundColor: "#f1f5f9",
                                                borderRadius: "6px",
                                                position: "absolute",
                                                bottom: 0,
                                                zIndex: 1,
                                                minHeight: item.capacity > 0 ? "4px" : "0"
                                            }}
                                        />

                                        {/* Barra de Ocupação Real (Cor) */}
                                        <div
                                            style={{
                                                width: "40%",
                                                height: `${Math.min(enrolledHeight, capHeight)}%`,
                                                backgroundColor: barColor,
                                                borderRadius: "6px",
                                                position: "absolute",
                                                bottom: 0,
                                                zIndex: 2,
                                                transition: "height 0.8s ease-in-out",
                                                opacity: isCurrentMonth ? 1 : 0.65
                                            }}
                                        />
                                        
                                        {/* Badge % Acima da Barra */}
                                        {item.capacity > 0 && (
                                            <span 
                                                className="position-absolute"
                                                style={{
                                                    bottom: `calc(${capHeight}% + 5px)`,
                                                    fontSize: "9px",
                                                    fontWeight: "bold",
                                                    color: barColor,
                                                    opacity: isCurrentMonth ? 1 : 0.6
                                                }}
                                            >
                                                {item.pct}%
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* Mês Label */}
                                    <span 
                                        className="position-absolute text-center mt-2" 
                                        style={{ 
                                            bottom: "-25px", 
                                            fontSize: "10px", 
                                            fontWeight: isCurrentMonth ? "800" : "600",
                                            color: isCurrentMonth ? "#3b82f6" : "#64748b"
                                        }}
                                    >
                                        {item.month.split('/')[0]}<span className="d-block text-muted" style={{ fontSize: '8px' }}>{item.month.split('/')[1]}</span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default OccupancyChart;
